import type { Copy } from '@/i18n/en'
import type { LocaleDescriptor } from '@/i18n/locales'
import { format } from '@/i18n/format'
import type { VoiceErrorCode } from './types'

/**
 * Turns an error code into words a person can act on, in their own language.
 *
 * Shared by the visible banner and the screen-reader alert region, which is
 * the whole point. They used to disagree: the banner rendered localised copy
 * while the alert announced `VoiceError.message`, an English developer string.
 * The most frequent trigger is not permission denial, it is "no Hausa voice
 * installed", which no browser ships, so essentially every Hausa session began
 * by announcing an English sentence into a Hausa speech engine inside a
 * document declared as Hausa.
 */
export interface ErrorDescription {
  title: string
  body: string
  /** Whether trying the same thing again could plausibly work. */
  retryable: boolean
}

export function describeVoiceError(
  code: VoiceErrorCode,
  copy: Copy,
  locale: LocaleDescriptor,
): ErrorDescription {
  switch (code) {
    case 'mic-denied':
      return {
        title: copy.ui.error.micDeniedTitle,
        body: copy.ui.error.micDeniedBody,
        // Browsers only re-prompt from a fresh gesture, so the retry control is
        // exactly the gesture that makes recovery possible.
        retryable: true,
      }
    case 'mic-missing':
    case 'mic-busy':
      return {
        title: copy.ui.error.micMissingTitle,
        body: copy.ui.error.micMissingBody,
        retryable: true,
      }
    case 'unsupported':
    case 'no-recognition':
      return {
        title: copy.ui.error.unsupportedTitle,
        body: copy.ui.error.unsupportedBody,
        retryable: false,
      }
    case 'no-voice':
      return {
        title: copy.ui.error.noVoiceTitle,
        body: format(copy.ui.error.noVoiceBody, { language: locale.nativeName }),
        retryable: false,
      }
    case 'network':
      return {
        title: copy.ui.error.offlineTitle,
        body: copy.ui.error.offlineBody,
        retryable: true,
      }
    case 'recognition-failed':
    case 'synthesis-failed':
      return {
        title: copy.ui.error.connectionFailedTitle,
        body: copy.ui.error.connectionFailedBody,
        retryable: true,
      }
    default:
      return {
        title: copy.ui.error.genericTitle,
        body: copy.ui.error.genericBody,
        retryable: true,
      }
  }
}
